import React, { createContext, useEffect, useState } from "react";
import { backend_url } from "../App";

export const ShopContext = createContext(null);

const ShopContextProvider = (props) => {
  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    fetch(`${backend_url}/allproducts`)
      .then((res) => res.json())
      .then((data) => setProducts(data))
      .catch((err) => {
        console.error("Failed to load products:", err);
        setProducts([]);
      });

    if (localStorage.getItem("auth-token")) {
      fetch(`${backend_url}/getcart`, {
        method: "POST",
        headers: {
          "auth-token": `${localStorage.getItem("auth-token")}`,
          "Content-Type": "application/json",
        },
      })
        .then((resp) => resp.json())
        .then((data) => {
          const backendCart =
            typeof data === "object" && !Array.isArray(data) && data !== null
              ? data
              : {};

          // Filter out any invalid entries (meter fabrics can have fractional quantities like 1.5)
          const cleanCart = Object.fromEntries(
            Object.entries(backendCart).filter(
              ([key, value]) =>
                Number.isInteger(Number(key)) &&
                Number.isFinite(Number(value)) &&
                Number(value) > 0
            )
          );

          setCartItems(cleanCart);
          sessionStorage.removeItem("cart");
        })
        .catch((err) => console.error("Cart fetch error:", err));
    } else {
      const sessionCart = JSON.parse(sessionStorage.getItem("cart")) || {};
      setCartItems(sessionCart);
    }
  }, []);

  const updateCartInSession = (newCart) => {
    setCartItems(newCart);
    sessionStorage.setItem("cart", JSON.stringify(newCart));
  };

  //Add to Cart (works even if user is not logged in)
  const addToCart = (itemId, step = 1) => {
    const newCart = { ...cartItems, [itemId]: (cartItems[itemId] || 0) + step };
    updateCartInSession(newCart);

    if (localStorage.getItem("auth-token")) {
      fetch(`${backend_url}/addtocart`, {
        method: "POST",
        headers: {
          "auth-token": localStorage.getItem("auth-token"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ itemId, quantity: step }),
      }).catch((err) => console.error("Error adding to cart:", err));
    }
  };

  //Remove from Cart (works even if user is not logged in)
  const removeFromCart = (itemId) => {
    if (cartItems[itemId] > 0) {
      const newCart = { ...cartItems };

      if (newCart[itemId] === 1) {
        // Set it to zero first to show feedback
        newCart[itemId] = 0;
        setCartItems({ ...newCart });

        // Delay removal to give visual feedback
        setTimeout(() => {
          delete newCart[itemId];
          setCartItems({ ...newCart });
          updateCartInSession(newCart);
        }, 200);
      } else {
        newCart[itemId] -= 1;
        setCartItems(newCart);
        updateCartInSession(newCart);
      }

      if (localStorage.getItem("auth-token")) {
        fetch(`${backend_url}/removefromcart`, {
          method: "POST",
          headers: {
            "auth-token": `${localStorage.getItem("auth-token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ itemId }),
        }).catch((err) => console.error("Error removing from cart:", err));
      }
    }
  };

  const deleteFromCart = async (itemId) => {
    const newCart = { ...cartItems };
    delete newCart[itemId];
    setCartItems(newCart);
    sessionStorage.setItem("cart", JSON.stringify(newCart));

    if (localStorage.getItem("auth-token")) {
      // Call removefromcart repeatedly until item gone
      while (cartItems[itemId] > 0) {
        await fetch(`${backend_url}/removefromcart`, {
          method: "POST",
          headers: {
            "auth-token": localStorage.getItem("auth-token"),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ itemId }),
        });
        cartItems[itemId]--; // decrement locally too
      }
    }
  };

  const getTotalCartAmount = () => {
    if (products.length === 0) return 0;

    const total = Object.keys(cartItems).reduce((sum, itemId) => {
      const product = products.find((p) => p.id === Number(itemId));
      if (!product) return sum; // Skip if product not found
      const discountedPrice =
        (product.old_price * (100 - product.new_price)) / 100;
      return sum + cartItems[itemId] * discountedPrice;
    }, 0);
    return Number(total.toFixed(2));
  };

  const clearCart = () => {
    // Clear from state and sessionStorage
    setCartItems({});
    sessionStorage.removeItem("cart");

    // If user is logged in, also clear from backend
    if (localStorage.getItem("auth-token")) {
      fetch(`${backend_url}/clearcart`, {
        method: "POST",
        headers: {
          "auth-token": localStorage.getItem("auth-token"),
          "Content-Type": "application/json",
        },
      })
        .then(() => {
          console.log("Cart cleared on backend");
        })
        .catch((err) => {
          console.error("Error clearing cart:", err);
        });
    }
  };

  const getTotalCartItems = () => {
    if (!cartItems || typeof cartItems !== "object") return 0;
    return Object.values(cartItems).reduce((total, qty) => {
      const quantity = Number(qty) || 0;
      return total + (quantity > 0 ? quantity : 0);
    }, 0);
  };

  const handleLogout = () => {
    localStorage.removeItem("auth-token");
    setCartItems({});
    sessionStorage.removeItem("cart");
  };

  const contextValue = {
    products,
    cartItems,
    filteredProducts,
    searchQuery,
    setSearchQuery,
    addToCart,
    removeFromCart,
    deleteFromCart,
    getTotalCartAmount,
    getTotalCartItems,
    setCartItems,
    clearCart,
    setFilteredProducts,
  };

  return (
    <ShopContext.Provider value={contextValue}>
      {props.children}
    </ShopContext.Provider>
  );
};

export default ShopContextProvider;
